import { type RunListing } from '@d2/schema';
import { useState, type CSSProperties, type ReactElement, type SyntheticEvent } from 'react';

import { type RunRefusalReason } from '../game/runs';
import {
  CODE_LABEL,
  NOTHING_OPEN,
  PRIVATE_NOTE,
  READING_NOTE,
  codeHintOf,
  describeRunRefusal,
  isJoining,
  joinName,
  lobbyStatusOf,
  partyLabel,
  runSummary,
} from './lobby-model';

export { type RunRefusalReason } from '../game/runs';

/**
 * The board a player finds a run on: what is open, and a field for a code.
 *
 * ## Two ways in, and they end in exactly the same place
 *
 * A row's Join and the code field both hand **a code** to the caller. Neither
 * of them produces an address, because a listing carries no room id at all
 * (`@d2/schema`'s `RunListing`) — the caller turns a code into a room id
 * through a request of its own, at the moment of the press, which is what makes
 * the fullness check the one that is true *now* rather than the one that was
 * true when the list was drawn.
 *
 * That is why there is no "join a random run" control and no `joinOrCreate`
 * anywhere near this: dropping a player into whichever room has a seat is a
 * different feature, and it is one nobody has designed (`TODO.md` #213).
 *
 * ## It draws and decides nothing
 *
 * The list is the server's — which runs are offered at all is `apps/game`'s
 * `listRuns`, and this file neither filters nor sorts it. The words are
 * `lobby-model.ts`'s. The one call that looks like a decision, `codeHintOf`, is
 * `@d2/schema`'s own `parseInviteCode` wearing a sentence, so a panel cannot
 * come to hold a second, stricter idea of what a code is.
 *
 * ## One press is one join, and the controls are genuinely `disabled`
 *
 * The same call `town-screen.tsx` makes for Descend and `vendor-panel.tsx` for
 * an exchange, and here the stake is the room's 409: two joins in flight are two
 * seat reservations for one character, and the second is answered *"this
 * character is already in a run"* — a refusal about this client's own doing,
 * shown to a player who merely pressed twice. So while the caller says a join is
 * in flight, **every** control on the board is inert, not only the row that was
 * pressed.
 *
 * The one thing that is *not* disabled is the code field's button for a code
 * that will not parse. That is deliberate and it is the same call
 * `vendor-panel.tsx` makes about affordability: a greyed control has nothing to
 * say for itself, and *"that is not a code"* in the live region is more than the
 * greying was ever going to tell them.
 *
 * ## Placeholder art
 *
 * The palette is `town-screen.tsx`'s, copied rather than imported, for the
 * reason that file gives: a shared style module written twice by two agents at
 * once is a merge conflict rather than a saving.
 */

const PARCHMENT = '#d8d2c4';
const INK = '#0b0b0d';
const PANEL = '#15151a';
const EDGE = '#3a3730';
const GOLD = '#c8a44c';
const DIM = '#6f6a5e';

const SECTION: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '0.75rem',
  padding: '0.9rem',
  border: `1px solid ${EDGE}`,
  borderRadius: 3,
  background: PANEL,
  color: PARCHMENT,
};

const HEADER: CSSProperties = { display: 'flex', alignItems: 'center', gap: '0.75rem' };
const TITLE: CSSProperties = { margin: 0, fontSize: '0.85rem', letterSpacing: '0.25em', textTransform: 'uppercase' };
const GROUP: CSSProperties = { display: 'flex', flexDirection: 'column', gap: '0.35rem' };
const QUIET: CSSProperties = { margin: 0, color: DIM, fontSize: '0.75rem' };

const ROW: CSSProperties = {
  display: 'flex',
  alignItems: 'baseline',
  gap: '0.5rem',
  padding: '0.3rem 0.4rem',
  border: `1px solid ${EDGE}`,
  borderRadius: 3,
  background: INK,
  fontSize: '0.8rem',
};

const PARTY: CSSProperties = { marginLeft: 'auto', color: DIM, fontSize: '0.75rem' };
const FIELD_ROW: CSSProperties = { display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' };
const LABEL: CSSProperties = { color: DIM, fontSize: '0.75rem', letterSpacing: '0.1em', textTransform: 'uppercase' };
const STATUS: CSSProperties = { minHeight: '1.25rem', margin: 0, color: GOLD, fontSize: '0.85rem' };

const INPUT: CSSProperties = {
  padding: '0.3rem 0.5rem',
  border: `1px solid ${EDGE}`,
  borderRadius: 3,
  background: INK,
  color: PARCHMENT,
  font: 'inherit',
  fontSize: '0.8rem',
  letterSpacing: '0.15em',
  textTransform: 'uppercase',
  width: '10rem',
};

const buttonStyle = (busy: boolean): CSSProperties => ({
  padding: '0.25rem 0.7rem',
  border: `1px solid ${busy ? EDGE : GOLD}`,
  borderRadius: 3,
  background: busy ? PANEL : EDGE,
  color: busy ? DIM : PARCHMENT,
  font: 'inherit',
  fontSize: '0.75rem',
  cursor: busy ? 'progress' : 'pointer',
});

export interface LobbyPanelProps {
  /** The open runs, or `null` because none have been read yet. An empty list is not `null`. */
  readonly runs: readonly RunListing[] | null;
  /** Why there is no list, when there is none and it is not merely late. */
  readonly failure?: RunRefusalReason | null;
  /** Read the directory again. There is no push channel; this is the whole of the refresh. */
  readonly onRefresh: () => void;
  /**
   * Join the run this **code** names — clicked from a row, or typed into the
   * field. Never an address: see the header.
   */
  readonly onJoin: (code: string) => void;
  /**
   * The code being resolved, or `null`. **One at a time**, and the caller owns
   * it: a latch inside the panel would outlive the caller's knowledge of the
   * request and leave a dead board.
   */
  readonly joining?: string | null;
  /**
   * How the last thing this board tried came to, as a value — a join that was
   * refused, or a refresh that was.
   *
   * One region for both, because they are one conversation about one board:
   * *"that party is full"* and *"the directory is not answering"* are answers to
   * presses a second apart, and a caller holding two of these would have to
   * invent a precedence between them. `failure` is the other half and is a
   * different question — it stands in for a board that has never been read at
   * all, where there is nothing to say a sentence *beside*.
   *
   * A value and never a sentence: a caller that passed prose could put
   * *"malformed-code"* on a screen in a town.
   */
  readonly note?: RunRefusalReason | null;
  /** A read of the directory is in flight, so asking again would be a second one. */
  readonly reading?: boolean;
}

const runRow = (
  run: RunListing,
  joining: string | null | undefined,
  onJoin: (code: string) => void,
): ReactElement => (
  <div key={run.code} data-testid={`lobby-run-${run.code}`} style={ROW}>
    <span>{runSummary(run)}</span>
    <span style={PARTY}>{partyLabel(run.party)}</span>
    <button
      type="button"
      // A real `disabled`, and set by *any* join being in flight rather than
      // only by this one. See the header.
      disabled={isJoining(joining)}
      aria-label={joinName(run)}
      style={buttonStyle(joining === run.code)}
      onClick={(): void => {
        // The code, and only ever the code. There is no room id on this row to
        // hand up instead, and that is the design rather than an omission.
        onJoin(run.code);
      }}
    >
      {joining === run.code ? 'Joining…' : 'Join'}
    </button>
  </div>
);

export const LobbyPanel = ({
  runs,
  failure = null,
  onRefresh,
  onJoin,
  joining = null,
  note = null,
  reading = false,
}: LobbyPanelProps): ReactElement => {
  // The field's own text, held here for the reason `SignInScreen` holds an
  // email: it is what has been typed so far, not a fact about the world, and
  // nothing above this panel has any use for it until it is submitted.
  const [typed, setTyped] = useState('');
  const busy = isJoining(joining);

  const submit = (event: SyntheticEvent<HTMLFormElement>): void => {
    event.preventDefault();

    // Blank is not a join. Everything else goes up as typed — what a code *is*
    // is `@d2/schema`'s to say, and the caller is what asks it.
    if (typed.trim() === '' || busy) return;

    onJoin(typed);
  };

  return (
    <section data-testid="lobby-panel" aria-label="Open runs" style={SECTION}>
      <header style={HEADER}>
        <h3 style={TITLE}>Open runs</h3>
        <button
          type="button"
          data-testid="lobby-refresh"
          disabled={reading || busy}
          style={{ ...buttonStyle(reading), marginLeft: 'auto' }}
          onClick={(): void => {
            onRefresh();
          }}
        >
          {reading ? 'Looking…' : 'Refresh'}
        </button>
      </header>

      {runs === null ? (
        /*
          A board nobody has read yet is not an empty board, and drawing the
          first as the second says "nobody is playing" about a request that is
          still in flight. When the read *failed*, the reason is said instead.
        */
        <p data-testid="lobby-reading" style={QUIET}>
          {failure === null ? READING_NOTE : describeRunRefusal(failure)}
        </p>
      ) : (
        <div data-testid="lobby-runs" role="group" aria-label="Runs you can join" style={GROUP}>
          {runs.length === 0 ? (
            <p data-testid="lobby-empty" style={QUIET}>
              {NOTHING_OPEN}
            </p>
          ) : (
            runs.map((run) => runRow(run, joining, onJoin))
          )}
        </div>
      )}

      {/*
        Said rather than left to be discovered: a private run is unlisted, not
        unjoinable, and a player who cannot find their friend's run would
        otherwise conclude this board is broken.
      */}
      <p style={QUIET}>{PRIVATE_NOTE}</p>

      <form style={GROUP} onSubmit={submit} noValidate>
        <div style={FIELD_ROW}>
          <label style={LABEL} htmlFor="lobby-code">
            {CODE_LABEL}
          </label>
          <input
            id="lobby-code"
            data-testid="lobby-code"
            type="text"
            name="code"
            autoComplete="off"
            spellCheck={false}
            // Pointed at the hint, so what is wrong with a code is read out as
            // part of the field rather than being a sentence somewhere near it.
            aria-describedby="lobby-code-hint"
            value={typed}
            style={INPUT}
            onChange={(event): void => {
              setTyped(event.target.value);
            }}
          />
          <button type="submit" data-testid="lobby-join-code" disabled={busy} style={buttonStyle(busy)}>
            Join by code
          </button>
        </div>
        {/*
          Kept in the document whatever it says, so the field's description does
          not appear and disappear under a reader as the text is typed.
        */}
        <p id="lobby-code-hint" data-testid="lobby-code-hint" style={QUIET}>
          {codeHintOf(typed)}
        </p>
      </form>

      {/*
        The board's own live region, and deliberately not town's: *"that party
        is full"* and *"that run is over"* are two conversations, and one region
        carrying both would let a refused join quietly overwrite the news of a
        death.
      */}
      <p data-testid="lobby-status" role="status" aria-live="polite" style={STATUS}>
        {lobbyStatusOf(joining, note)}
      </p>
    </section>
  );
};
